export type Testimonial = {
  /** Display name, e.g. "Ravi K." */
  name: string;
  /** Where they own and where they live, e.g. "Hyderabad plot owner · based in the US" */
  location: string;
  /** The plot itself, e.g. "Residential plot, Kokapet" */
  plot: string;
  quote: string;
  /** Path under /public/avatars. Self-hosted Notion-style avatar. */
  avatar: string;
};

type TestimonialCardProps = {
  testimonial: Testimonial;
  /**
   * The bento's anchor tile. Sets the quote in the display face at a larger
   * size and leads with a drawn quote mark. One per grid — more than one and
   * the grid loses its focal point.
   */
  featured?: boolean;
  className?: string;
};

function Attribution({ testimonial, compact }: { testimonial: Testimonial; compact?: boolean }) {
  return (
    <div className="mt-6 flex items-center gap-3">
      <img
        src={testimonial.avatar}
        alt=""
        aria-hidden
        width={48}
        height={48}
        loading="lazy"
        decoding="async"
        className={`shrink-0 rounded-full bg-secondary ${compact ? 'size-10' : 'size-12'}`}
      />
      {/* Wraps rather than truncates — at 264px the narrow tiles clipped these to
          "…based…", which reads as broken rather than terse. */}
      <div className="min-w-0">
        <p className="font-display text-sm font-medium text-foreground">{testimonial.name}</p>
        <p className="text-xs leading-snug text-foreground/60">{testimonial.location}</p>
        {!compact && <p className="text-xs leading-snug text-foreground/60">{testimonial.plot}</p>}
      </div>
    </div>
  );
}

/**
 * One tile in the testimonial bento. Hairline border + card surface rather than
 * a shadowed float, so the tiles sit in the same material language as the rest
 * of the page.
 */
export function TestimonialCard({ testimonial, featured, className }: TestimonialCardProps) {
  return (
    <figure
      className={`flex flex-col rounded-lg border border-border bg-card p-6 sm:p-7 ${className ?? ''}`}
    >
      {featured && (
        <span
          aria-hidden
          className="type-wonk mb-2 font-display text-6xl leading-none text-ring/45"
        >
          &ldquo;
        </span>
      )}
      <blockquote
        className={
          featured
            ? 'flex-1 font-display text-xl leading-relaxed text-foreground/90 sm:text-2xl'
            : 'flex-1 leading-relaxed text-foreground/75'
        }
      >
        {testimonial.quote}
      </blockquote>
      <figcaption>
        <Attribution testimonial={testimonial} compact={!featured} />
      </figcaption>
    </figure>
  );
}
